// Validaciones puras de coherencia normativa entre tipo de producto, impuesto y código Cat.54.
// Sin React, sin imports de UI, sin side effects.
// Reutilizado por: formulario de producto, importación masiva, validación defensiva en emisión.

import { CATALOGO_54_DETRACCIONES } from './catalogos-tributarios';
import { esCodigoHabilitadoParaEmision } from './calculo-detraccion';
import type { CodigoDetraccionTributaria } from './tipos-catalogos-tributarios';

// ─── Tipos ────────────────────────────────────────────────────────────

export type TipoProductoDetraccion = 'BIEN' | 'SERVICIO';

/** Afectación IGV resuelta desde el impuesto del producto. */
export type AfectacionIgv = 'gravado' | 'exonerado' | 'inafecto' | 'exportacion' | 'desconocido';

export interface ResultadoValidacionDetraccion {
  valido: boolean;
  /** Si es true, la combinación debe bloquearse. Si es false, solo es advertencia. */
  esBloqueo: boolean;
  mensaje?: string;
}

// ─── Resolver afectación desde impuesto string ────────────────────────

/**
 * Convierte el string de impuesto del formulario/importación a un enum interno.
 * Ejemplos: 'IGV (18.00%)' → 'gravado', 'Exonerado (0.00%)' → 'exonerado'
 */
export function resolverAfectacionDesdeImpuesto(impuesto: string): AfectacionIgv {
  const lower = impuesto.toLowerCase();
  if (!lower.trim()) return 'desconocido';
  if (lower.includes('exporta')) return 'exportacion';
  if (lower.includes('inafecto')) return 'inafecto';
  if (lower.includes('exonerado')) return 'exonerado';
  if (lower.includes('igv') || lower.includes('gravado')) return 'gravado';
  return 'desconocido';
}

// ─── Clasificaciones compatibles por tipo de producto ─────────────────
// Basadas en el campo `clasificacion` de CATALOGO_54_DETRACCIONES.

const CLASIFICACIONES_BIEN: ReadonlySet<string> = new Set([
  'Bien',
  'Bien inmueble',
  'Recurso hidrobiológico',
]);

const CLASIFICACIONES_SERVICIO: ReadonlySet<string> = new Set([
  'Servicio',
  'Transporte pasajeros',
  'Transporte carga',
  'Construcción',
]);

export function clasificacionesCompatiblesPorTipoProducto(
  tipoProducto: TipoProductoDetraccion,
): ReadonlySet<string> {
  return tipoProducto === 'BIEN' ? CLASIFICACIONES_BIEN : CLASIFICACIONES_SERVICIO;
}

// ─── Consultas sobre el catálogo ──────────────────────────────────────

/** Devuelve los códigos activos y habilitados para emisión, filtrados por tipo de producto. */
export function obtenerCodigosDetraccionCompatibles(
  tipoProducto: TipoProductoDetraccion,
  catalogo: CodigoDetraccionTributaria[] = CATALOGO_54_DETRACCIONES,
): CodigoDetraccionTributaria[] {
  const compatibles = clasificacionesCompatiblesPorTipoProducto(tipoProducto);
  return catalogo.filter(
    (c) => esCodigoHabilitadoParaEmision(c.codigo) && compatibles.has(c.clasificacion),
  );
}

/**
 * Devuelve los códigos compatibles con la combinación tipo de producto + impuesto.
 * Usa `afectacionesCompatibles` del catálogo para un filtro exacto sin heurísticas de texto.
 * Si el impuesto es inafecto, exportación o desconocido, retorna lista vacía (sin opciones seguras).
 */
export function obtenerCodigosDetraccionCompatiblesConImpuesto(
  tipoProducto: TipoProductoDetraccion,
  impuesto: string,
  catalogo?: CodigoDetraccionTributaria[],
): CodigoDetraccionTributaria[] {
  const base = obtenerCodigosDetraccionCompatibles(tipoProducto, catalogo);
  if (!impuesto.trim()) return base;

  const afectacion = resolverAfectacionDesdeImpuesto(impuesto);

  if (afectacion === 'inafecto' || afectacion === 'exportacion') {
    return [];
  }

  if (afectacion === 'desconocido') {
    return base;
  }

  return base.filter((c) => c.afectacionesCompatibles.includes(afectacion));
}

/** Indica si el código Cat.54 es compatible con el tipo de producto. */
export function esCodigoCompatibleConTipoProducto(
  codigoCat54: string,
  tipoProducto: TipoProductoDetraccion,
): boolean {
  const entrada = CATALOGO_54_DETRACCIONES.find((c) => c.codigo === codigoCat54);
  if (!entrada) return false;
  return clasificacionesCompatiblesPorTipoProducto(tipoProducto).has(entrada.clasificacion);
}

// ─── Validación por impuesto (string del formulario) ──────────────────

/** Recibe impuesto como string del formulario/importación (ej. 'IGV (18.00%)').  */
export function validarCoherenciaCodigoConImpuesto(
  codigoCat54: string,
  impuesto: string,
): ResultadoValidacionDetraccion {
  const entrada = CATALOGO_54_DETRACCIONES.find((c) => c.codigo === codigoCat54);
  if (!entrada) {
    return { valido: false, esBloqueo: true, mensaje: 'Código de detracción no encontrado en el Catálogo 54.' };
  }

  if (!impuesto.trim()) return { valido: true, esBloqueo: false };

  const afectacion = resolverAfectacionDesdeImpuesto(impuesto);

  if (afectacion === 'inafecto') {
    return {
      valido: false,
      esBloqueo: true,
      mensaje: 'No hay códigos de detracción habilitados para productos inafectos.',
    };
  }

  if (afectacion === 'exportacion') {
    return {
      valido: false,
      esBloqueo: true,
      mensaje: 'No hay códigos de detracción habilitados para operaciones de exportación.',
    };
  }

  if (afectacion === 'desconocido') return { valido: true, esBloqueo: false };

  // gravado | exonerado: verificar contra afectacionesCompatibles del catálogo
  if (!entrada.afectacionesCompatibles.includes(afectacion)) {
    const labelCodigo = afectacion === 'gravado' ? 'exonerados' : 'gravados con el IGV';
    const labelProducto = afectacion === 'gravado' ? 'gravado con IGV' : 'exonerado';
    return {
      valido: false,
      esBloqueo: true,
      mensaje:
        `El código de detracción seleccionado corresponde a bienes/servicios ${labelCodigo}, ` +
        `pero el producto está configurado como ${labelProducto}.`,
    };
  }

  return { valido: true, esBloqueo: false };
}

// ─── Validación por igvType (enum del motor de comprobantes) ──────────

export type IgvTypeDetraccion = 'igv18' | 'igv10' | 'exonerado' | 'inafecto';

/** Versión de validarCoherenciaCodigoConImpuesto que acepta igvType del CartItem. */
export function validarCoherenciaCodigoConIgvType(
  codigoCat54: string,
  igvType: IgvTypeDetraccion,
): ResultadoValidacionDetraccion {
  const mapeo: Record<IgvTypeDetraccion, string> = {
    igv18: 'IGV (18.00%)',
    igv10: 'IGV (10.00%)',
    exonerado: 'Exonerado (0.00%)',
    inafecto: 'Inafecto (0.00%)',
  };
  return validarCoherenciaCodigoConImpuesto(codigoCat54, mapeo[igvType]);
}

// ─── Validación por tipo de producto ──────────────────────────────────

/** Valida coherencia entre clasificación Cat.54 y tipo de producto (BIEN/SERVICIO). */
export function validarCoherenciaCodigoConTipoProducto(
  codigoCat54: string,
  tipoProducto: TipoProductoDetraccion,
): ResultadoValidacionDetraccion {
  if (esCodigoCompatibleConTipoProducto(codigoCat54, tipoProducto)) {
    return { valido: true, esBloqueo: false };
  }
  const tipoLabel = tipoProducto === 'BIEN' ? 'bien' : 'servicio';
  return {
    valido: false,
    esBloqueo: true,
    mensaje: `El código de detracción seleccionado no corresponde al tipo de producto (${tipoLabel}).`,
  };
}

// ─── Validación completa combinada ────────────────────────────────────

export interface ParamsValidacionDetraccion {
  codigoCat54: string;
  tipoProducto: TipoProductoDetraccion;
  impuesto: string;
}

/**
 * Ejecuta todas las validaciones de coherencia en orden de prioridad.
 * Retorna el primer error bloqueante encontrado, o `valido: true` si no hay problemas.
 */
export function validarCoherenciaCompleta(
  params: ParamsValidacionDetraccion,
): ResultadoValidacionDetraccion {
  const { codigoCat54, tipoProducto, impuesto } = params;

  const vTipo = validarCoherenciaCodigoConTipoProducto(codigoCat54, tipoProducto);
  if (!vTipo.valido && vTipo.esBloqueo) return vTipo;

  const vIgv = validarCoherenciaCodigoConImpuesto(codigoCat54, impuesto);
  if (!vIgv.valido && vIgv.esBloqueo) return vIgv;

  if (!vTipo.valido) return vTipo;
  if (!vIgv.valido) return vIgv;

  return { valido: true, esBloqueo: false };
}
